import { useState, useEffect, useCallback } from 'react';
import { gqlClient } from '../components/AppProvider';
import { queryBlobStorageFileVersionList } from '../gql/QueryBlobStorageFileVersionList';
import { BlobStorageModel, BlobDocumentType } from '../gql-types.generated';

/**
 * Custom hook for fetching blob storage file versions
 * Handles loading state, errors, and provides a refetch function
 * 
 * @param blobDocumentId - The blob document ID to fetch versions for
 * @param blobDocumentType - The type of blob document (SEF, TransactionMap, etc.)
 * @returns Object containing fileVersions, isLoading, error, and refetch function
 */
export const useBlobStorageFileVersions = (
    blobDocumentId: string | undefined,
    blobDocumentType: BlobDocumentType,
    shouldFetch: boolean = true
) => {
    const [fileVersions, setFileVersions] = useState<BlobStorageModel[] | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | undefined>(undefined);

    const fetchVersions = useCallback(async () => {
        if (!blobDocumentId || !shouldFetch) {
            setFileVersions(undefined);
            return;
        }

        setIsLoading(true);
        setError(undefined);

        try {
            const result = await queryBlobStorageFileVersionList(gqlClient, {
                blobDocumentId,
                blobDocumentType
            });

            if (result?.nodes) {
                setFileVersions(result.nodes as BlobStorageModel[]);
            } else {
                setFileVersions([]);
            }
        } catch (err) {
            console.error('Error fetching blob storage file versions:', err);
            setError(err as Error);
            setFileVersions(undefined);
        } finally {
            setIsLoading(false);
        }
    }, [blobDocumentId, blobDocumentType]);

    useEffect(() => {
        fetchVersions();
    }, [fetchVersions]);

    const refetch = useCallback(() => {
        fetchVersions();
    }, [fetchVersions]);

    return {
        fileVersions,
        isLoading,
        error,
        refetch
    };
};